import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	InputLabel,
	FormControl,
	Box,
	IconButton,
	MenuItem,
	FilledInput,
	Divider,
	FormHelperText,
} from '@mui/material';
import Select, { SelectProps } from '@mui/material/Select';

import { useEffect, useState } from 'react';

import { circleDeleteWhiteIcon } from '../../assets/images/index';

import styles from './SelectDropdown.module.scss';

const {
	backgroundColor,
	borderColor,
	borderColorPrimary,
	backgroundColorGrey,
	paddingY,
	paddingX,
} = styles;

type ISelectProps = Omit<SelectProps, 'onChange'> & {
	onChange: (val: string, obj?: any) => void;
	clearable?: boolean;
	mode?: 'standard' | 'filled';
	options: ISelectOption[];
	helperText?: string | null;
	showLabel?: boolean;
	disabled?: boolean;
	isFromRelMapping?: boolean;
	isSelectPadding?: boolean;
};

export interface ISelectOption {
	value: string | number;
	label: string;
	obj?: any;
}
/**
 * This component will have select with close icon at end.
 * User can select the single option from menu and allowed to delete the selected option
 * @param props Props for select
 * @returns component
 */

const checkClearable = (clearable: boolean | undefined, otherProps: any) =>
	clearable && (!otherProps || !otherProps.disabled);

const checkBackGroundColor = (isFromRelMapping: boolean) =>
	isFromRelMapping ? backgroundColorGrey : backgroundColor;

const checkClearableValue = (
	clearable: boolean | undefined,
	otherProps: any,
	value: any,
) => clearable && value && (!otherProps || !otherProps.disabled);
const getBorderColor = (isFromRelMapping: boolean) =>
	isFromRelMapping ? borderColorPrimary : borderColor;
const getHeight = (isFromRelMapping: boolean) =>
	isFromRelMapping ? '40px' : '58px';

const SelectDropdown = ({
	clearable,
	options,
	mode = 'filled',
	onChange,
	disabled = false,
	showLabel = true,
	value,
	isFromRelMapping = false,
	isSelectPadding = false,
	...otherProps
}: ISelectProps) => {
	const [selectedValue, setSelectedValue] = useState<string | null>(null);

	useEffect(() => {
		setSelectedValue((value as string) || null);
	}, [value]);

	const handleChange = (e: any) => {
		const value = e.target.value as string;
		const selectedObj = options.find((ele) => ele.value === value);
		onChange(value, selectedObj?.obj || '');
	};
	const handleClear = () => {
		onChange('');
	};

	const [open, setOpen] = useState<boolean>(false);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen((prev) => !prev);
	};

	const isClearable = checkClearable(clearable, otherProps);
	const isClearableValue = checkClearableValue(clearable, otherProps, value);

	return mode === 'filled' ? (
		<FormControl sx={{ width: '100%' }} variant="filled">
			<InputLabel
				className={`${styles['select-filled-label']} ${
					disabled ? styles['disabled-input'] : ''
				}`}
				shrink
				id="filled-select-adornment"
				variant="filled"
				onClick={handleOpen}
			>
				{otherProps.label}
				{otherProps.required ? (
					<span className={styles['select-label-asterisk']}> *</span>
				) : null}
			</InputLabel>
			<Select
				open={open}
				onOpen={handleOpen}
				onClose={handleClose}
				labelId="filled-select-adornment"
				disabled={disabled}
				IconComponent={ExpandMoreRoundedIcon}
				{...otherProps}
				value={selectedValue || ''}
				className={otherProps.className}
				sx={{
					backgroundColor: checkBackGroundColor(isFromRelMapping),
					border: '1px solid',
					borderColor: getBorderColor(isFromRelMapping),
					height: getHeight(isFromRelMapping),
					...(isSelectPadding && {
						'& .MuiSelect-select': {
							padding: `${paddingY} ${paddingX} ${paddingY}`,
						},
					}),
				}}
				data-testid="select-dropdown"
				variant="filled"
				inputProps={{
					'aria-label': 'Without label',
					style: { paddingBottom: '0px' },
					...otherProps.inputProps,
				}}
				onChange={handleChange}
				displayEmpty
				disableUnderline
				endAdornment={
					isClearable ? (
						<Box sx={{ display: 'flex', mr: 1 }}>
							<IconButton onClick={handleClear} sx={{ padding: 0 }}>
								<img src={circleDeleteWhiteIcon} alt="cancel" />
							</IconButton>
							<Divider
								sx={{ height: 35, m: 2, borderRightWidth: 2 }}
								orientation="vertical"
							/>
						</Box>
					) : null
				}
				MenuProps={{
					PaperProps: {
						className: styles['menu-paper'],
					},
				}}
			>
				{options.map((option) => (
					<MenuItem
						key={`dropdown-${otherProps.label}-${option.value}`}
						value={option.value}
					>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	) : (
		<Box className={styles['select-wrapper-dark-border']}>
			<FormControl sx={{ width: '100%', display: 'contents' }}>
				{showLabel && (
					<Box className={styles['select-label']}>
						{otherProps.label}
						{otherProps.required ? (
							<span className={styles['select-label-asterisk']}> *</span>
						) : null}
					</Box>
				)}
				<Select
					IconComponent={ExpandMoreRoundedIcon}
					{...otherProps}
					value={selectedValue || ''}
					className={otherProps.className}
					data-testid="select-dropdown"
					sx={{
						backgroundColor: checkBackGroundColor(isFromRelMapping),
						border: '2px',
						borderColor: borderColorPrimary,
					}}
					SelectDisplayProps={{
						style: { padding: '7px' },
					}}
					inputProps={{
						'aria-label': 'Without label',
						...otherProps.inputProps,
					}}
					onChange={handleChange}
					displayEmpty
					disableUnderline
					fullWidth
					input={<FilledInput hiddenLabel={false} />}
					endAdornment={
						isClearableValue ? (
							<Box sx={{ display: 'flex', mr: 1 }}>
								<IconButton onClick={handleClear} sx={{ padding: 0 }}>
									<img src={circleDeleteWhiteIcon} alt="cancel" />
								</IconButton>
								<Divider
									sx={{
										height: 35,
										ml: 2,
										mr: 2,
										borderRightWidth: 2,
										zIndex: 1,
									}}
									orientation="vertical"
								/>
							</Box>
						) : null
					}
					MenuProps={{
						PaperProps: {
							className: styles['menu-paper'],
						},
					}}
				>
					{options.map((option) => (
						<MenuItem
							key={`dropdown-${otherProps.label}-${option.value}`}
							value={option.value}
						>
							{option.label}
						</MenuItem>
					))}
				</Select>
				{otherProps.helperText && (
					<FormHelperText error>{otherProps.helperText}</FormHelperText>
				)}
			</FormControl>
		</Box>
	);
};

export default SelectDropdown;
